import { forwardRef, useRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { prefetchPath } from '@/utils/route-prefetch';

/**
 * A drop-in replacement for react-router's <Link> that warms the
 * destination route's lazy chunk only on user-intent signals:
 *   1. mouseenter / focus — the moment the user shows intent.
 *   2. touchstart         — covers tap on touch devices.
 *
 * We used to warm on IntersectionObserver visibility too, but that caused
 * every visible header/footer link to fetch its route chunk on every page
 * load — effectively pulling the whole app onto every page. Idle-time
 * warmup for a small allow-list of top routes still runs via
 * `prefetchTopRoutes()` in route-prefetch.
 */
const PrefetchLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, onMouseEnter, onFocus, onTouchStart, ...rest }, forwardedRef) => {
    const innerRef = useRef<HTMLAnchorElement | null>(null);
    const targetPath = typeof to === 'string' ? to : to.pathname ?? '';

    // Stitch the forwarded ref to our internal ref.
    const setRef = (node: HTMLAnchorElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    return (
      <Link
        ref={setRef}
        to={to}
        onMouseEnter={(e) => {
          prefetchPath(targetPath);
          onMouseEnter?.(e);
        }}
        onFocus={(e) => {
          prefetchPath(targetPath);
          onFocus?.(e);
        }}
        onTouchStart={(e) => {
          prefetchPath(targetPath);
          onTouchStart?.(e);
        }}
        {...rest}
      />
    );
  },
);

PrefetchLink.displayName = 'PrefetchLink';

export default PrefetchLink;